import React from 'react';

/** Extract a focused snippet around the first occurrence of any query term. */
export function extractSnippet(content: string, query: string, maxLen = 150): string {
  // Clean up OCR artefacts: collapse whitespace, strip control chars
  const cleaned = content.replace(/\s+/g, ' ').trim();
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const lower = cleaned.toLowerCase();
  let earliest = -1;
  for (const w of words) {
    const idx = lower.indexOf(w);
    if (idx !== -1 && (earliest === -1 || idx < earliest)) earliest = idx;
  }
  // Semantic search may not have a literal match — fall back to start
  if (earliest === -1) earliest = 0;
  const start = Math.max(0, earliest - 30);
  const end = Math.min(cleaned.length, start + maxLen);
  const snippet = cleaned.slice(start, end);
  return (start > 0 ? '...' : '') + snippet.trim() + (end < cleaned.length ? '...' : '');
}

/**
 * Estimate which page a keyword actually falls on within a multi-page chunk.
 * Interpolates linearly between pageNumber and endPageNumber based on where
 * the keyword appears in the chunk text.
 */
export function estimateKeywordPage(
  content: string,
  query: string,
  pageNumber: number | null,
  endPageNumber: number | null,
): number | undefined {
  if (pageNumber == null) return undefined;
  if (endPageNumber == null || endPageNumber <= pageNumber) return pageNumber;
  const idx = content.toLowerCase().indexOf(query.trim().toLowerCase());
  if (idx === -1) return pageNumber;
  const fraction = idx / content.length;
  return pageNumber + Math.round(fraction * (endPageNumber - pageNumber));
}

/** Wrap matching query terms in <mark> tags for highlighting. */
export function highlightTerms(text: string, query: string): React.ReactNode {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return text;
  const pattern = new RegExp(
    `(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'gi',
  );
  const parts = text.split(pattern);
  return parts.map((part, i) =>
    pattern.test(part)
      ? <mark key={i} className="bg-amber-200/80 dark:bg-amber-700/50 text-inherit rounded-sm px-0.5">{part}</mark>
      : part,
  );
}
